import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, invokeEdgeFunction } from '@/lib';
import {
  listVaultEntries,
  getVaultEntry,
  getActiveAdminId,
  markVaultEntryStale,
  subscribeVault,
  isPinSwitchDeviceEnabled,
  enablePinSwitchOnDevice,
  disablePinSwitchOnDevice,
  initSessionVault,
  captureCurrentSession,
  isDeviceLocked,
  lockDevice,
  unlockDevice,
  type VaultEntry,
} from '@/lib/session-vault';

/** Outcome of a fast-switch attempt. */
export interface PinSwitchResult {
  success: boolean;
  /**
   * - `invalid_or_locked` — wrong PIN, no PIN set, or server lockout (the server
   *   never distinguishes these, by design).
   * - `password_required` — the vaulted session can't be re-activated (expired /
   *   missing refresh token); the target must do a full password login.
   */
  reason?: 'invalid_or_locked' | 'password_required';
  /** ISO timestamp until which the target is locked, when known. */
  lockedUntil?: string | null;
}

interface VerifyPinResponse {
  success: boolean;
  reason?: string;
  locked_until?: string | null;
}

/**
 * PIN fast-switch controller (spec Phase 2): exposes the vaulted admins on this
 * device and `switchTo(targetAdminId, pin)`, which verifies the PIN server-side,
 * re-activates the target's vaulted session, and forces a role re-verification.
 *
 * No UI consumes this yet — the switch screen, idle-lock, and provisioning UI
 * arrive in later phases.
 */
export function usePinSwitch() {
  const queryClient = useQueryClient();
  const [isSwitching, setIsSwitching] = useState(false);
  const switchingRef = useRef(false);

  // Subscribe to the vault so the list stays live across tabs. listVaultEntries
  // returns a referentially-stable array, so it's a valid snapshot getter and a
  // safe server snapshot (returns the EMPTY constant when there's no window).
  const entries = useSyncExternalStore<VaultEntry[]>(
    subscribeVault,
    listVaultEntries,
    listVaultEntries,
  );
  const [activeAdminId, setActiveAdminId] = useState<string | null>(getActiveAdminId());
  useEffect(() => subscribeVault(() => setActiveAdminId(getActiveAdminId())), []);

  const switchTo = useCallback(
    async (targetAdminId: string, pin: string): Promise<PinSwitchResult> => {
      // Block double-submit while a verification is in flight.
      if (switchingRef.current) {
        return { success: false, reason: 'invalid_or_locked' };
      }
      switchingRef.current = true;
      setIsSwitching(true);
      try {
        // 1. Server-side PIN check (called as the currently active admin = actor).
        const { data, error } = await invokeEdgeFunction<VerifyPinResponse>(
          'verify-admin-pin',
          { body: { targetAdminId, pin } },
        );
        if (error || !data?.success) {
          return {
            success: false,
            reason: 'invalid_or_locked',
            lockedUntil: data?.locked_until ?? null,
          };
        }

        // 2. Re-activate the target's vaulted session. The server never returns
        //    the target tokens; we hold them locally.
        const entry = getVaultEntry(targetAdminId);
        if (!entry || entry.stale) {
          return { success: false, reason: 'password_required' };
        }

        const { error: setError } = await supabase.auth.setSession({
          access_token: entry.accessToken,
          refresh_token: entry.refreshToken,
        });
        if (setError) {
          // Expired / invalid refresh token → fail closed to password login.
          markVaultEntryStale(targetAdminId);
          return { success: false, reason: 'password_required' };
        }

        // 3. Confirm the live identity and re-verify role from the server.
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user || userData.user.id !== targetAdminId) {
          markVaultEntryStale(targetAdminId);
          return { success: false, reason: 'password_required' };
        }

        // 4. Drop all cached data so permission/admin hooks refetch as the new
        //    identity (authorization always comes from the live session).
        queryClient.clear();

        // 5. Clear the lock — an authorized admin is back in.
        unlockDevice();

        return { success: true };
      } finally {
        switchingRef.current = false;
        setIsSwitching(false);
      }
    },
    [queryClient],
  );

  return {
    /** Vaulted admins on this device (newest-active first), incl. stale flag. */
    vaultedAdmins: entries,
    /** The admin currently active in this client (to exclude from the list). */
    activeAdminId,
    isSwitching,
    switchTo,
  };
}

/**
 * Per-device enrolment for PIN fast-switch. Enabling starts the vault (it was a
 * no-op at boot when the device was off) and snapshots the current session so
 * the active admin is immediately switchable.
 */
export function usePinSwitchDevice() {
  const [enabled, setEnabled] = useState(() => isPinSwitchDeviceEnabled());
  const [isEnabling, setIsEnabling] = useState(false);

  const enable = useCallback(async () => {
    setIsEnabling(true);
    try {
      enablePinSwitchOnDevice();
      initSessionVault();
      await captureCurrentSession();
      setEnabled(true);
    } catch (error) {
      // Roll back so a failed capture doesn't leave the device half-enrolled.
      disablePinSwitchOnDevice();
      setEnabled(false);
      throw error;
    } finally {
      setIsEnabling(false);
    }
  }, []);

  const disable = useCallback(() => {
    disablePinSwitchOnDevice(); // also wipes vaulted sessions from this device
    setEnabled(false);
  }, []);

  return { enabled, isEnabling, enable, disable };
}

/**
 * Reactive lock state plus lock/unlock controls. `locked` is enforced by
 * ProtectedRoute (app routes redirect to the switch screen while locked).
 */
export function useDeviceLock() {
  const locked = useSyncExternalStore(subscribeVault, isDeviceLocked, () => false);
  return {
    locked,
    lock: useCallback(() => lockDevice(), []),
    unlock: useCallback(() => unlockDevice(), []),
  };
}
